import type { MouseEvent, PointerEvent } from 'react';

const pointerStarts = new WeakMap<
  HTMLElement,
  {
    x: number;
    y: number;
    ranges: Array<{
      startContainer: Node;
      startOffset: number;
      endContainer: Node;
      endOffset: number;
    }>;
  }
>();

export function rememberSelectionOnPointerDown(event: PointerEvent<HTMLElement>): void {
  const selection = event.currentTarget.ownerDocument.getSelection();
  const ranges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) => {
        const range = selection.getRangeAt(index);
        return {
          startContainer: range.startContainer,
          startOffset: range.startOffset,
          endContainer: range.endContainer,
          endOffset: range.endOffset,
        };
      })
    : [];
  pointerStarts.set(event.currentTarget, { x: event.clientX, y: event.clientY, ranges });
}

/** Finishing a text-selection drag must not open the inline field's editor. */
export function isTextSelectionClick(event: MouseEvent<HTMLElement>): boolean {
  const start = pointerStarts.get(event.currentTarget);
  pointerStarts.delete(event.currentTarget);
  if (event.detail === 0 || !start) return false; // Keyboard and assistive-technology activation.
  const selection = event.currentTarget.ownerDocument.getSelection();
  if (!selection || selection.isCollapsed) return false;
  if (
    Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 4 &&
    start.ranges.length === selection.rangeCount &&
    start.ranges.every((previous, index) => {
      const current = selection.getRangeAt(index);
      return (
        previous.startContainer === current.startContainer &&
        previous.startOffset === current.startOffset &&
        previous.endContainer === current.endContainer &&
        previous.endOffset === current.endOffset
      );
    })
  ) {
    return false; // An intentional click after selecting or copying text.
  }
  for (let index = 0; index < selection.rangeCount; index += 1) {
    if (selection.getRangeAt(index).intersectsNode(event.currentTarget)) return true;
  }
  return false;
}
